import "./calendar.scss";
import React, { useEffect, useState } from "react";
import Select from "../select/select";
import IconButton from "../../icon-button/icon-button";
import { useTranslate } from "../../../features/translation/hooks/use-translation";

const SUFFIX_DAYS = [1, 0, 6, 5, 4, 3, 2];

const YEAR_OPTIONS_THRESHOLD = 3;

const pad = (num) => num.toString().padStart(2, "0");

const getDateString = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return "";
  }
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};


const isDateEqual = (date1, date2) => {
  if (!date1 || !date2) {
    return false;
  }
  return date1.toString() === date2.toString();
};

const resetTimeToMidnight = (date) => {
  return new Date(date.setHours(0, 0, 0, 0));
};

const Calendar = (props) => {
  //console.debug("Render calendar: " + props.name);

  const { t } = useTranslate();

  /* Limitations on date/month/year options, set in date options, month/year options*/
  const [earliestDate, setEarliestDate] = useState(
    resetTimeToMidnight(props.min ? new Date(props.min) : new Date())
  );

  // TODO:
  const [latestDate, setLatestDate] = useState(
    props.max
      ? new Date(props.max)
      : new Date(earliestDate.getFullYear() + YEAR_OPTIONS_THRESHOLD, 11, 31)
  );

  // Props <= defaultDate

  const [month, setMonth] = useState(
    props.value
      ? new Date(props.value).getMonth()
      : props.min
      ? new Date(props.min).getMonth()
      : props.defaultValue
      ? new Date(props.defaultValue).getMonth()
      : new Date().getMonth()
  );
  const [year, setYear] = useState(
    props.value
      ? new Date(props.value).getFullYear()
      : props.min
      ? new Date(props.min).getFullYear()
      : props.defaultValue
      ? new Date(props.defaultValue).getFullYear()
      : new Date().getFullYear()
  );

  const [yearOptions, setYearOptions] = useState(
    Array.from(
      { length: latestDate.getFullYear() - earliestDate.getFullYear() + 1 },
      (_, i) => ({
        label: earliestDate.getFullYear() + i,
        value: earliestDate.getFullYear() + i,
      })
    )
  );
  const [monthOptions, setMonthOptions] = useState(
    Array.from({ length: 12 }, (_, i) => ({
      label: t(`input.custom.hiddenCombiCalendar.months.${i+1}`), //[i],
      value: i,
      disabled: i < new Date().getMonth(),
    }))
  );

  const [disablePrev, setDisablePrev] = useState(false);
  const [disableNext, setDisableNext] = useState(false);

  const {
    error,
    displayError,
    message,
    helpertext,
    label,
    tooltip,
    defaultValue,
    ...inputProps //onchange. onblur, value, etc
  } = props;

  const Day = ({ date }) => {
    const dateTime = getDateString(date);
    return (
      <label
        htmlFor={`${props.id || props.name}_${dateTime}`}
        className={`day ${
          date < earliestDate ||
          date.getFullYear() !== year ||
          date.getMonth() !== month
            ? "passive"
            : ""
        } ${isDateEqual(earliestDate, date) ? "current" : ""}
                            ${
                              props.value === `${dateTime}`
                                ? "selected selected-start selected-end"
                                : ""
                            }
                        tet`}
      >
        <time
          dateTime={dateTime}
          // TODO: set selected
          disabled={date < earliestDate}
          tabIndex="0"
        >
          <input
            type="radio"
            name={props.name}
            id={`${props.id || props.name}_${dateTime}`}
            {...inputProps}
            disabled={props.disabled}
            onChange={props.onChange}
            value={dateTime}
          />
          {date.getDate()}
        </time>
      </label>
    );
  };

  const onPrevious = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const onNext = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  useEffect(() => {
    if (yearOptions.length > 0) {
      if (
        year === yearOptions[yearOptions.length - 1].value &&
        month === monthOptions[monthOptions.length - 1].value
      ) {
        setDisableNext(true);
      } else if (
        year === yearOptions[0].value &&
        month === monthOptions.filter((o) => o.disabled === false)[0].value
      ) {
        setDisablePrev(true);
      } else if (disableNext) {
        setDisableNext(false);
      } else if (disablePrev) {
        setDisableNext(false);
        setDisablePrev(false);
      }
    }
  }, [month, year]);

  useEffect(() => {
    const newOptions = Array.from({ length: 12 }, (_, i) => ({
        label: t(`input.custom.hiddenCombiCalendar.months.${i+1}`), //[i],
        value: i,
      disabled:
        (parseInt(year) === earliestDate.getFullYear() &&
          i < earliestDate.getMonth()) ||
        (parseInt(year) === latestDate.getFullYear() &&
          i > latestDate.getMonth()),
    }));
    setMonthOptions(newOptions);

    if (
      yearOptions[0] &&
      year === yearOptions[0].value &&
      newOptions[month].disabled
    ) {
      setMonth(new Date().getMonth());
    }
  }, [year]);

  //  console.log(props)
  // TODO: if small screen, just show ordinary calendar

  const suffixIndex = new Date(year, month + 1, 1).getDay();
  const suffixLength = SUFFIX_DAYS[suffixIndex] ?? 0;

  return (
    <div className="single-cal" disabled={props.disabled}>
      <fieldset className={`input-checkbox-group `} style={props.style}>
        <legend className="label">{label}</legend>

        <span id={`${props.id || props.name}_help`} className="helpertext">
          {helpertext}
        </span>
      </fieldset>
      <div
        className={`calendar ${props.disabled === true ? "disabled" : ""}`}
        disabled={props.disabled}
      >
        <div className="month">
          <div className="indicator indicator-select ">
            <IconButton
              icon="chevron-left"
              onClick={() => onPrevious()}
              disabled={disablePrev || props.disabled}
            />
            <div
              style={{
                display: "inline-flex",
                gap: "var(--space-2)",
                justifyContent: "center",
              }}
            >
              <Select
                name="viewMonth"
                value={month}
                options={monthOptions}
                disabled={props.disabled}
                onChange={(e) => {
                  setMonth(parseInt(e.target.value));
                }}
              />
              <Select
                name="viewYear"
                value={year}
                options={yearOptions}
                disabled={props.disabled}
                onChange={(e) => {
                  setYear(parseInt(e.target.value));
                }}
              />
            </div>
            <IconButton
              icon="chevron"
              onClick={() => onNext()}
              disabled={disableNext || props.disabled}
            />
          </div>
          <div className="weekdays">
          {[1, 2, 3, 4, 5, 6, 7].map(i => (
            <span key={i} data-title={t(`input.custom.hiddenCombiCalendar.weekDays.${i}`)}>
              {t(`input.custom.hiddenCombiCalendar.weekDays.${i}`).slice(0, 2)}
            </span>
          ))}
          </div>
          <div className="days">
            {Array.from(
              { length: new Date(year, month, 0).getDay() },
              (_, i) => (
                <Day
                  date={
                    new Date(
                      year,
                      month - 1,
                      new Date(year, month, 0).getDate() - i
                    )
                  }
                  key={`${props.name}_${year}-${month - 1}-${
                    new Date(year, month, 0).getDate() - i
                  }`}
                />
              )
            ).reverse()}

            {Array.from(
              { length: new Date(year, month + 1, 0).getDate() },
              (_, i) => (
                <Day
                  date={new Date(year, month, i + 1)}
                  key={`${props.name}_${year}-${month + 1}-${i + 1}`}
                />
              )
            )}

            {Array.from(
              { length: suffixLength},
              (_, i) => (
                <Day
                  date={new Date(year, month + 1, i + 1)}
                  key={`${props.name}_${year}-${month + 2}-${i + 1}`}
                />
              )
            )}
          </div>
        </div>
      </div>
      {props.error && props.displayError && (
        <p
          id={`${props.id || props.name}_error`}
          className="message"
          style={{
            display: "flex",
            gap: "var(--space-2)",
            color: "var(--color-functional-error-600)",
            alignItems: "center",
          }}
        >
          <span
            className="icon xsmall"
            style={{
              WebkitMaskImage:
                "url(https://static.tui.com/assets/v2/icons/tui-light/exclamation-mark-circled-filled.svg)",
              maskImage:
                "url(https://static.tui.com/assets/v2/icons/tui-light/exclamation-mark-circled-filled.svg)",
              color: "currentColor",
            }}
          ></span>
          {props.error}
        </p>
      )}
    </div>
  );
};

export default Calendar;
